import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { orderBy, toArray, get } from 'lodash-es';
import {
  PRICE_CHECKER_CREATIVE_ID,
  VEHICLE_SELECT_MODE_MAP,
  PriceCheckerEntities,
} from 'site-modules/shared/constants/price-checker';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  InventoryVehiclePaths,
  InventoryVehicleEntities,
  InventoryVehicleModel,
} from 'client/data/models/inventory-vehicle';
import { buildStylesPath, VehicleModel, VehicleEntities } from 'client/data/models/vehicle-v2';

import { getSubmodelIdentifier } from 'site-modules/shared/utils/core-page/page-helpers';
import { StylePriceChecker } from 'site-modules/price-checker/components/style-price-checker/style-price-checker';
import { VinPriceChecker } from 'site-modules/price-checker/components/vin-price-checker/vin-price-checker';
import { CompareOffers } from 'site-modules/price-checker/components/compare-offers/compare-offers';
import { HowToCheck } from 'site-modules/price-checker/components/how-to-check/how-to-check';

import './price-checker-card.scss';

export function PriceCheckerCardUI({
  vehicleSelectMode = VEHICLE_SELECT_MODE_MAP.MMY,
  hasChangeModel = true,
  className = 'mb-1_5',
  areVehicleParamsReady = false,
  onModeChange,
  updateVinParams,
  vehicleParams,
  vinParams,
  initialPrices = undefined,
  updateVehicleParams,
  updateLeadFormParams = null,
  leadFormCreativeId = null,
  hasEmbeddedLeadForm = false,
  showCalculators = false,
  isMobile = false,
  zipCode = null,
  sharedDeal = null,
  resetSharedDealParams = null,
  visitorId = null,
  vinStyleTmvDealData = null,
  styles = null,
  isTestDeal = false,
  isCore6830International = false,
  pageName = null,
  sessionId = null,
  hideHeading = false,
}) {
  let leadFormStyle = styles && (styles.find(({ id }) => id === get(vehicleParams, 'style.id')) || styles[0]);
  if (vehicleSelectMode === VEHICLE_SELECT_MODE_MAP.VIN) {
    const vinStyleId = get(vinStyleTmvDealData, 'vinStyleId');
    const currentStyle =
      vinParams.styles && vinParams.styles.find(({ id }) => (vinStyleId || vinParams.styleId) === id);
    leadFormStyle = {
      makeName: vinParams.makeName,
      modelName: vinParams.modelName,
      year: vinParams.year,
      trimName: get(currentStyle, 'trim'),
      make: { name: vinParams.makeName },
      model: { name: vinParams.modelName },
      submodels: {
        name: get(currentStyle, 'submodelName'),
        slug: getSubmodelIdentifier(get(currentStyle, 'submodelName'), vinParams.modelName),
      },
      id: vinParams.styleId,
    };
  }

  return (
    <div className={classnames('price-checker-card', className)} data-tracking-parent={PRICE_CHECKER_CREATIVE_ID}>
      {!hideHeading && (
        <div className="card-heading mb-2" style={{ lineHeight: '1.875rem', fontSize: '1.5rem', fontWeight: 'bold' }}>
          Are you getting a good price? Check it before you buy.
        </div>
      )}
      {areVehicleParamsReady && (
        <section
          className={classnames({
            'd-none': vehicleSelectMode === VEHICLE_SELECT_MODE_MAP.VIN,
          })}
        >
          <StylePriceChecker
            vehicleParams={vehicleParams}
            initialPrices={initialPrices}
            updateVehicleParams={updateVehicleParams}
            onModeChange={onModeChange}
            isMobile={isMobile}
            leadFormCreativeId={leadFormCreativeId}
            hasChangeModel={hasChangeModel}
            hasEmbeddedLeadForm={hasEmbeddedLeadForm}
            zipCode={zipCode}
            sharedDeal={sharedDeal}
            resetSharedDealParams={resetSharedDealParams}
            showCalculators={showCalculators}
            visitorId={visitorId}
            styles={styles}
            isTestDeal={isTestDeal}
            pageName={pageName}
            sessionId={sessionId}
            isCore6830International={isCore6830International}
          />
        </section>
      )}
      <section
        className={classnames({
          'd-none': vehicleSelectMode === VEHICLE_SELECT_MODE_MAP.MMY,
        })}
      >
        <VinPriceChecker
          updateVinParams={updateVinParams}
          initialPrices={initialPrices}
          vinParams={vinParams}
          isMobile={isMobile}
          leadFormCreativeId={leadFormCreativeId}
          hasEmbeddedLeadForm={hasEmbeddedLeadForm}
          zipCode={zipCode}
          sharedDeal={sharedDeal}
          resetSharedDealParams={resetSharedDealParams}
          showCalculators={showCalculators}
          visitorId={visitorId}
          vinStyleTmvDealData={vinStyleTmvDealData}
          onModeChange={onModeChange}
          isTestDeal={isTestDeal}
          pageName={pageName}
          sessionId={sessionId}
          isCore6830International={isCore6830International}
        />
      </section>
      <HowToCheck mode={vehicleSelectMode} onModeChange={onModeChange} vehicleParams={vehicleParams} />
      <CompareOffers
        style={leadFormStyle}
        isMobile={isMobile}
        leadFormCreativeId={leadFormCreativeId}
        hasEmbeddedLeadForm={hasEmbeddedLeadForm}
        updateLeadFormParams={updateLeadFormParams}
      />
    </div>
  );
}

PriceCheckerCardUI.propTypes = {
  onModeChange: PropTypes.func.isRequired,
  updateVehicleParams: PropTypes.func.isRequired,
  updateVinParams: PropTypes.func.isRequired,
  className: PropTypes.string,
  vehicleParams: PropTypes.shape({
    make: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
    }),
    model: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
    }),
    year: PropTypes.shape({
      year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    style: PropTypes.shape({
      id: PropTypes.number,
    }),
  }).isRequired,
  vinParams: PropTypes.shape({
    vin: PropTypes.string,
    makeName: PropTypes.string,
    modelName: PropTypes.string,
    styles: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        trim: PropTypes.string,
        submodelName: PropTypes.string,
      })
    ),
    styleId: PropTypes.number,
    year: PropTypes.number,
  }).isRequired,
  updateLeadFormParams: PropTypes.func,
  vehicleSelectMode: PropTypes.string,
  leadFormCreativeId: PropTypes.string,
  hasChangeModel: PropTypes.bool,
  areVehicleParamsReady: PropTypes.bool,
  initialPrices: PropTypes.shape({
    userDealerPrice: PropTypes.number,
    userMsrp: PropTypes.number,
  }),
  hasEmbeddedLeadForm: PropTypes.bool,
  showCalculators: PropTypes.bool,
  isMobile: PropTypes.bool,
  zipCode: PropTypes.string,
  sharedDeal: PriceCheckerEntities.SharedDataEntity,
  resetSharedDealParams: PropTypes.func,
  visitorId: PropTypes.string,
  vinStyleTmvDealData: InventoryVehicleEntities.VinStyleTmvDealData,
  styles: VehicleEntities.Styles,
  isTestDeal: PropTypes.bool,
  isCore6830International: PropTypes.bool,
  pageName: PropTypes.string,
  sessionId: PropTypes.string,
  hideHeading: PropTypes.bool,
};

export const PriceCheckerCard = connectToModel(PriceCheckerCardUI, {
  vinStyleTmvDealData: bindToPath(
    ({ vinParams: { vin, styleId } }) => vin && InventoryVehiclePaths.buildVinStyleTmvDealPath({ vin, styleId }),
    InventoryVehicleModel
  ),
  styles: bindToPath(
    ({ vehicleParams: { make, model, year } }) =>
      !!(make && model && year) && buildStylesPath({ make: make.slug, model: model.slug, year: year.year }),
    VehicleModel,
    styles => orderBy(toArray(styles), 'name')
  ),
});
